import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { prebuiltAutomations, type PrebuiltAutomation } from "@/lib/api";
import Spinner from "@/components/Spinner";
import {
  ArrowLeft, Search, Zap, Clock, GitBranch, Mail,
  Download, Filter, ChevronDown, Globe, Sparkles,
  ShoppingCart, Monitor, Wrench, Building2, MessageSquare,
} from "lucide-react";

const INDUSTRIES = [
  { value: "",          label: "All",           icon: Globe },
  { value: "general",   label: "Universal",     icon: Sparkles },
  { value: "ecommerce", label: "E-commerce",    icon: ShoppingCart },
  { value: "saas",      label: "SaaS",          icon: Monitor },
  { value: "agency",    label: "Agency / B2B",  icon: Building2 },
];

const TRIGGER_LABELS: Record<string, { label: string; icon: any; color: string }> = {
  subscriber_added: { label: "New Subscriber",   icon: Zap,           color: "bg-amber-50 text-amber-600" },
  tag_added:        { label: "Tag Added",         icon: Zap,           color: "bg-purple-50 text-purple-600" },
  inactivity:       { label: "Inactivity",        icon: Clock,         color: "bg-red-50 text-red-600" },
  reply_received:   { label: "Reply Received",    icon: MessageSquare, color: "bg-blue-50 text-blue-600" },
  webhook_received: { label: "Webhook",           icon: GitBranch,     color: "bg-teal-light text-teal" },
  email_opened:     { label: "Email Opened",      icon: Mail,          color: "bg-emerald-50 text-emerald-600" },
  link_clicked:     { label: "Link Clicked",      icon: Mail,          color: "bg-coral-light text-coral" },
  campaign_sent:    { label: "Campaign Sent",     icon: Mail,          color: "bg-indigo-50 text-indigo-600" },
  manual:           { label: "Manual",            icon: Zap,           color: "bg-slate/10 text-slate" },
};

const DIFFICULTY_BADGE: Record<string, string> = {
  beginner: "bg-emerald-50 text-emerald-700 border-emerald-200",
  intermediate: "bg-amber-50 text-amber-700 border-amber-200",
  advanced: "bg-rose-50 text-rose-700 border-rose-200",
};

export default function PrebuiltAutomationsPage() {
  const navigate = useNavigate();
  const [automations, setAutomations] = useState<PrebuiltAutomation[]>([]);
  const [loading, setLoading] = useState(true);
  const [industry, setIndustry] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await prebuiltAutomations.list({ industry: industry || undefined, search: search || undefined });
      setAutomations(res.automations);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [industry, search]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="flex items-center gap-3 mb-2">
        <button onClick={() => navigate("/automations")} className="p-2 rounded-lg hover:bg-black/[0.04] transition-colors">
          <ArrowLeft size={18} className="text-slate" />
        </button>
        <div>
          <h1 className="text-2xl font-display font-bold text-ink">Automation Templates</h1>
          <p className="text-slate text-sm mt-0.5">Pre-built workflows — install, customize, and activate.</p>
        </div>
      </div>

      {/* Search + Filter */}
      <div className="mt-6 mb-6 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate/50" />
          <input
            type="text" value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search automations..."
            className="w-full pl-9 pr-4 py-2.5 rounded-lg border border-sand bg-white text-sm font-body focus:outline-none focus:ring-2 focus:ring-coral/30 focus:border-coral transition-all"
          />
        </div>
        <button onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-medium transition-all ${showFilters ? "bg-coral text-white border-coral" : "border-sand bg-white text-slate hover:border-coral/40"}`}>
          <Filter size={14} /> Filter {industry && "• 1"} <ChevronDown size={14} className={`transition-transform ${showFilters ? "rotate-180" : ""}`} />
        </button>
      </div>

      {showFilters && (
        <div className="mb-6 flex flex-wrap gap-2 pb-4 border-b border-sand">
          {INDUSTRIES.map((ind) => {
            const Icon = ind.icon; const active = industry === ind.value;
            return (
              <button key={ind.value} onClick={() => setIndustry(ind.value)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${active ? "bg-coral text-white border-coral shadow-sm" : "bg-white border-sand text-slate hover:border-coral/40"}`}>
                <Icon size={13} /> {ind.label}
              </button>
            );
          })}
        </div>
      )}

      {!loading && <p className="text-xs text-slate mb-4">{automations.length} automation{automations.length !== 1 ? "s" : ""} available</p>}

      {loading && <div className="flex justify-center py-20"><Spinner /></div>}

      {!loading && automations.length === 0 && (
        <div className="text-center py-20">
          <GitBranch size={40} className="mx-auto text-slate/30 mb-4" />
          <p className="text-slate text-sm">No automations found. Try adjusting your filters.</p>
        </div>
      )}

      {!loading && automations.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {automations.map((a) => {
            const trigger = TRIGGER_LABELS[a.trigger_type] || TRIGGER_LABELS.manual;
            const TrigIcon = trigger.icon;
            return (
              <Link key={a.id} to={`/automations/prebuilt/${a.slug}`}
                className="group block bg-white rounded-xl border border-sand hover:border-coral/40 hover:shadow-md transition-all p-5">
                <div className="flex items-center justify-between mb-3">
                  <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${trigger.color}`}>
                    <TrigIcon size={11} /> {trigger.label}
                  </span>
                  <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${DIFFICULTY_BADGE[a.difficulty] || ""}`}>
                    {a.difficulty}
                  </span>
                </div>
                <h3 className="font-display font-bold text-ink text-base leading-snug group-hover:text-coral transition-colors">{a.name}</h3>
                <p className="text-slate text-xs mt-1.5 leading-relaxed line-clamp-2">{a.description}</p>
                <div className="flex items-center gap-4 mt-4 pt-3 border-t border-sand/60">
                  <span className="flex items-center gap-1 text-[11px] text-slate"><GitBranch size={12} /> {a.step_count} steps</span>
                  {a.install_count > 0 && (
                    <span className="flex items-center gap-1 text-[11px] text-slate ml-auto"><Download size={11} /> {a.install_count}</span>
                  )}
                </div>
                {a.tags && a.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-3">
                    {a.tags.slice(0, 3).map(tag => (
                      <span key={tag} className="text-[10px] text-slate/70 bg-paper px-1.5 py-0.5 rounded">{tag}</span>
                    ))}
                  </div>
                )}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
